import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { message } from '@client/components/_ui/feedback/message';
import { ContactTable } from '@client/components/contacts/ContactTable';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { isTruthy } from '@shared/utils';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { claimFormStore } from '../../../store';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/contacts/suppliers/reassign/$supplierId')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Supplier contacts',
        },
    },
    params: {
        parse({ supplierId }) {
            return { supplierId: BigInt(supplierId) };
        },
    },
    component: ReassignSupplierContacts,
});

function ReassignSupplierContacts() {
    const orgId = useOrgId();
    const claim = claimFormStore.use.formData();
    const isReadonly = claimFormStore.use.isReadonly();
    const { supplierId } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const { mutate, isPending } = trpc.claim.reassignClaimSupplierContacts.useMutation();
    const reload = claimFormStore.use.reload();
    const navigate = useNavigate();

    const [selectedRowKeys, setSelectedRowKeys] = useState<bigint[]>([
        ...new Set(
            claim.ClaimSuppliers.filter((item) => item.supplierId === supplierId)
                .map(({ contactId }) => contactId)
                .filter(isTruthy),
        ),
    ]);

    useEffect(() => {
        setSelectedRowKeys([
            ...new Set(
                claim.ClaimSuppliers.filter((item) => item.supplierId === supplierId)
                    .map(({ contactId }) => contactId)
                    .filter(isTruthy),
            ),
        ]);
    }, [claim.ClaimSuppliers]);

    useEffect(() => {
        if (isReadonly) {
            navigate({ moveInMatches: -1 });
        }
    }, [isReadonly]);

    return (
        <PageController route={Route}>
            <OverlayPage
                title="Supplier contacts"
                size="large"
                extra={
                    <Button.Add.Link
                        label="New contact"
                        variant="tertiary"
                        to="/console/$orgId/claims/details/$id/contacts/suppliers/reassign/$supplierId/add"
                        params={{
                            orgId,
                            id: claim.id,
                            supplierId,
                        }}
                    />
                }
                afterClose={() => navigate({ moveInMatches: -1 })}
                footer={({ onClose }) => (
                    <>
                        <Button onClick={onClose}>Close</Button>
                        <Button
                            type="primary"
                            loading={isPending}
                            onClick={() => {
                                message.loading('Saving...');

                                console.log(selectedRowKeys, '');

                                mutate(
                                    {
                                        claimId: claim.id,
                                        supplierId,
                                        contactIds: selectedRowKeys,
                                    },
                                    {
                                        onSuccess() {
                                            message.success('Saved.');
                                            void reload();
                                            void trpcUtils.contact.invalidate();
                                            onClose();
                                        },
                                    },
                                );
                            }}
                        >
                            Save
                        </Button>
                    </>
                )}
            >
                <Outlet />
                <ContactTable
                    type="SUPPLIER"
                    entity="supplier"
                    entityId={supplierId}
                    rowSelection={{
                        preserveSelectedRowKeys: true,
                        selectedRowKeys,
                        onChange(selectedRowKeys) {
                            setSelectedRowKeys(selectedRowKeys as bigint[]);
                        },
                    }}
                    showActionColumn={false}
                />
            </OverlayPage>
        </PageController>
    );
}
